import { MediaWidget } from '@/components/events/detail/tabs/common/media/media.widget.tsx';
import { getObjectId } from '@/helpers/object.id.helper';
import {
  buildPlaylistMediaRows,
  getSessionPlaylist,
  getSessionPlaylistMedia,
  playlistAllowsContributorAddMedia,
  playlistAllowsContributorReorder,
} from '@/helpers/flow-media-playlist.helper';
import type { SessionPlaylistSource } from '@/helpers/flow-media-playlist.helper';
import type { MediaListContext } from '@/components/events/detail/tabs/common/media/media.list';
import type { MediaParentEntity } from '@/types/dtos/media/media-parent-entity.enum';
import PlaylistMediaSettingsHeaderActions from '@/components/media/playlist-media-settings-switches';
import { useTranslations } from '@/i18n/use-translations';
import type { MediaListDisplayVariant } from '@/components/flows/detail/flow-detail.types';

type SessionPlaylistMediaWidgetProps = {
  session: SessionPlaylistSource;
  context: MediaListContext;
  contextParentId?: string;
  contextParentType?: MediaParentEntity;
  /** Flow slot id when session playlist is shown inside flow control. */
  flowSlotId?: string;
  widgetTitle?: string;
  liveReady?: boolean;
  enabled?: boolean;
  enablePlayerSocket?: boolean;
  disableBorder?: boolean;
  disableBackground?: boolean;
  disableHover?: boolean;
  disableShadow?: boolean;
  scrollableList?: boolean;
  disabled?: boolean;
  requestButtonLabel?: string;
  requestSubmitLabel?: string;
  createSubmitLabel?: string;
  disablePlayerQuery?: boolean;
  /** Hide create/reorder policy switches (e.g. contributor role details modal). */
  hideMediaSettingsSwitches?: boolean;
  displayVariant?: MediaListDisplayVariant;
};

export function SessionPlaylistMediaWidget({
  session,
  context,
  contextParentId,
  contextParentType = 'Session',
  flowSlotId,
  widgetTitle,
  liveReady = false,
  enabled = true,
  enablePlayerSocket = false,
  disableBorder,
  disableBackground,
  disableHover,
  disableShadow,
  scrollableList,
  disabled,
  requestButtonLabel,
  requestSubmitLabel,
  createSubmitLabel,
  disablePlayerQuery,
  hideMediaSettingsSwitches = false,
  displayVariant = 'default',
}: SessionPlaylistMediaWidgetProps) {
  const isFlowControl = displayVariant === 'flowControl';
  const t = useTranslations();
  const parentId = session.origin ? getObjectId(session.origin as Parameters<typeof getObjectId>[0]) : session.id;
  const playlist = getSessionPlaylist(session);
  const media = getSessionPlaylistMedia(session);
  const playlistRows = buildPlaylistMediaRows(playlist, media);

  // Contributor dashboard only — not flow Control/Perform (those hide switches via isFlowControl).
  const isContributorFacing = hideMediaSettingsSwitches && !isFlowControl;
  const stored = {
    allowSelfCreate: session.mediaSettings?.speakers?.allowSelfCreate ?? true,
    allowReorder: session.mediaSettings?.speakers?.allowReorder ?? true,
  };
  const speakersEffective = session.effectiveMediaSettings?.speakers;
  // Contributor dashboard: deny create/reorder when effective settings are unknown.
  const effectiveCreate = speakersEffective?.allowSelfCreate ?? (isContributorFacing ? false : stored.allowSelfCreate);
  const effectiveReorder = speakersEffective?.allowReorder ?? (isContributorFacing ? false : stored.allowReorder);
  const effective = speakersEffective ?? { allowSelfCreate: effectiveCreate, allowReorder: effectiveReorder };
  const canEditSession = session.mediaSettingsCapabilities?.canEditSession ?? false;
  const canAddMedia = playlistAllowsContributorAddMedia(
    effectiveCreate,
    playlist,
    isContributorFacing ? false : session.mediaSettingsCapabilities?.canEdit,
  );
  const canReorderPlaylist = playlistAllowsContributorReorder(
    effectiveReorder,
    playlist,
    isContributorFacing ? false : session.mediaSettingsCapabilities?.canEdit,
  );
  const disabledTooltip = !canEditSession
    ? session.isInLibrary
      ? t('media.settings.tooltip.disabled.sessionOverrideLibrary')
      : t('media.settings.tooltip.disabled.sessionOverrideFlow')
    : undefined;

  return (
    <MediaWidget
      media={media}
      playlist={playlist}
      playlistRows={playlistRows}
      parentId={parentId}
      parentType="Session"
      context={context}
      contextParentId={contextParentId ?? parentId}
      contextParentType={contextParentType}
      playlistScope={playlist?.scope}
      playlistSlotId={flowSlotId ?? playlist?.slotId}
      widgetTitle={widgetTitle}
      liveReady={liveReady}
      enablePlayerSocket={enablePlayerSocket}
      enabled={enabled}
      contributorCanAdd={isFlowControl ? false : canAddMedia}
      canReorderPlaylist={isFlowControl ? false : canReorderPlaylist}
      showRequestButton={isFlowControl ? false : isContributorFacing ? canAddMedia : true}
      displayVariant={displayVariant}
      disableBorder={disableBorder}
      disableBackground={disableBackground}
      disableHover={disableHover}
      disableShadow={disableShadow}
      scrollableList={scrollableList}
      disabled={disabled}
      requestButtonLabel={requestButtonLabel}
      requestSubmitLabel={requestSubmitLabel}
      createSubmitLabel={createSubmitLabel}
      disablePlayerQuery={disablePlayerQuery}
      settingsHeaderActions={
        hideMediaSettingsSwitches || isFlowControl ? undefined : (
          <PlaylistMediaSettingsHeaderActions
            mode="session"
            sessionId={parentId}
            stored={stored}
            effective={effective}
            canEdit={canEditSession}
            disabledTooltip={disabledTooltip}
          />
        )
      }
      contributorFacing={isContributorFacing}
    />
  );
}
